import { useState, useEffect } from "react";
import React from "react";
import { ArrowLeft, Zap, Thermometer, Gauge, Wifi, AlertTriangle, CheckCircle, MapPin, Calendar, Clock, ArrowUp, ArrowDown, Pause } from "lucide-react";
import { Link } from "react-router-dom";

export default function TveDemo() {
    const [selectedElevator, setSelectedElevator] = useState(1);
    const [elevators, setElevators] = useState([]);
    const [loading, setLoading] = useState(false);

    // Datos simulados de elevadores (ya que no tenemos API real para TVE)
    const simulatedElevators = [
        { 
            id: 1, 
            name: "Elevador Principal", 
            floor: 8, 
            maxFloor: 15,
            status: "operativo", 
            location: "Torre A", 
            lastUpdate: "1 min",
            voltage: 220,
            current: 12.5,
            temperature: 24,
            trips: 156,
            maintenance: "OK"
        },
        { 
            id: 2, 
            name: "Elevador Secundario", 
            floor: 3, 
            maxFloor: 15,
            status: "operativo", 
            location: "Torre A", 
            lastUpdate: "2 min",
            voltage: 218,
            current: 11.8,
            temperature: 26,
            trips: 89,
            maintenance: "OK"
        },
        { 
            id: 3, 
            name: "Elevador de Carga", 
            floor: 1, 
            maxFloor: 10,
            status: "mantenimiento", 
            location: "Torre B", 
            lastUpdate: "15 min",
            voltage: 215,
            current: 0,
            temperature: 22,
            trips: 45,
            maintenance: "Programado"
        },
        { 
            id: 4, 
            name: "Elevador Ejecutivo", 
            floor: 12, 
            maxFloor: 15,
            status: "operativo", 
            location: "Torre C", 
            lastUpdate: "30 seg",
            voltage: 222,
            current: 13.2,
            temperature: 25,
            trips: 203,
            maintenance: "OK"
        }
    ];

    const generateElevatorData = async () => {
        setLoading(true);
        // Simular delay de API
        setTimeout(() => {
            // Simular pequeñas variaciones en los datos
            const updatedElevators = simulatedElevators.map(elevator => ({
                ...elevator,
                floor: Math.max(1, Math.min(elevator.maxFloor, elevator.floor + Math.floor(Math.random() * 3) - 1)),
                voltage: elevator.voltage + (Math.random() * 4 - 2),
                current: elevator.status === "operativo" ? elevator.current + (Math.random() * 2 - 1) : 0,
                temperature: elevator.temperature + (Math.random() * 2 - 1),
                trips: elevator.trips + Math.floor(Math.random() * 2)
            }));
            
            setElevators(updatedElevators);
            setLoading(false);
        }, 1000);
    };

    useEffect(() => {
        generateElevatorData();
        const fetchInterval = setInterval(() => {
            generateElevatorData();
        }, 15000); // Actualizar cada 15 segundos
        return () => clearInterval(fetchInterval);
    }, []);

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'operativo':
            case 'funcionando':
            case 'activo':
                return 'text-green-600 bg-green-50 border-green-200';
            case 'mantenimiento':
            case 'revision':
                return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'fuera_servicio':
            case 'error':
            case 'falla':
                return 'text-red-600 bg-red-50 border-red-200';
            default: 
                return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'operativo':
            case 'funcionando':
            case 'activo':
                return <CheckCircle className="w-4 h-4" />;
            case 'mantenimiento':
            case 'revision':
            case 'fuera_servicio':
            case 'error':
            case 'falla':
                return <AlertTriangle className="w-4 h-4" />;
            default: 
                return <CheckCircle className="w-4 h-4" />;
        }
    };

    const getMovementIcon = (elevator) => {
        if (elevator.status !== "operativo") return <Pause className="w-4 h-4 text-gray-400" />;
        const movement = Math.random();
        if (movement > 0.6) return <ArrowUp className="w-4 h-4 text-blue-500" />;
        if (movement < 0.4) return <ArrowDown className="w-4 h-4 text-blue-500" />;
        return <Pause className="w-4 h-4 text-gray-500" />;
    };

    // Calcular estadísticas en tiempo real
    const getStats = () => {
        if (elevators.length === 0) {
            return {
                totalElevators: 0,
                operational: 0,
                averageTrips: 0,
                systemHealth: 0
            };
        }

        const totalElevators = elevators.length;
        const operational = elevators.filter(e => e.status === "operativo").length;
        const averageTrips = Math.round(elevators.reduce((sum, e) => sum + e.trips, 0) / totalElevators);
        const systemHealth = Math.round((operational / totalElevators) * 100);

        return { totalElevators, operational, averageTrips, systemHealth };
    };

    const stats = getStats();

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-violet-100">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/60">
                <div className="w-full flex h-20 items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <Link to="/" className="flex items-center gap-2 text-purple-600 hover:text-purple-800 transition-colors">
                            <ArrowLeft className="w-5 h-5" />
                            <span className="font-medium">Volver</span>
                        </Link>
                        <div className="h-6 w-px bg-gray-300"></div>
                        <div className="flex items-center gap-2">
                            <Zap className="w-6 h-6 text-purple-600" />
                            <span className="text-xl font-semibold text-gray-900">Sistema TVE - Elevadores Inteligentes</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        <span>Sistema Activo</span>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="relative overflow-hidden">
                {/* Background Effects */}
                <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-violet-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>

                <div className="relative container mx-auto px-6 py-12 max-w-7xl">
                    {/* Hero Section */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-100 to-violet-100 text-purple-800 rounded-full text-sm font-medium mb-6">
                            <MapPin className="w-4 h-4 mr-2" />
                            CABA, Argentina
                        </div>
                        <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-gray-900 mb-6">
                            Demo <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-violet-600">TVE</span>
                        </h1>
                        <p className="max-w-3xl mx-auto text-xl text-gray-600 leading-relaxed mb-8">
                            Sistema inteligente de monitoreo para elevadores verticales y horizontales en entornos residenciales y comerciales.
                            Control total en tiempo real de variables críticas y de mantenimiento.
                        </p>
                    </div>

                    {/* Stats Overview */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-violet-600 rounded-xl flex items-center justify-center">
                                    <Zap className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : stats.totalElevators}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Elevadores Monitoreados</h3>
                            <p className="text-xs text-green-600">
                                {loading ? "Cargando..." : `${stats.totalElevators} unidades activas`}
                            </p>
                        </div>

                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
                                    <CheckCircle className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : stats.operational}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Operativos</h3>
                            <p className={`text-xs ${
                                loading ? "text-gray-400" :
                                stats.systemHealth > 80 ? "text-green-600" :
                                stats.systemHealth > 50 ? "text-yellow-600" : "text-red-600"
                            }`}>
                                {loading ? "Cargando..." : `${stats.systemHealth}% del sistema`}
                            </p>
                        </div>

                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                                    <Gauge className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : stats.averageTrips}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Viajes Promedio</h3>
                            <p className="text-xs text-blue-600">Por día</p>
                        </div>

                        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl flex items-center justify-center">
                                    <Wifi className="w-6 h-6 text-white" />
                                </div>
                                <span className="text-2xl font-bold text-gray-900">
                                    {loading ? "..." : `${stats.systemHealth}%`}
                                </span>
                            </div>
                            <h3 className="text-sm font-medium text-gray-600 mb-1">Salud del Sistema</h3>
                            <p className={`text-xs ${
                                loading ? "text-gray-400" :
                                stats.systemHealth > 80 ? "text-green-600" :
                                stats.systemHealth > 50 ? "text-yellow-600" : "text-red-600"
                            }`}>
                                {loading ? "Cargando..." :
                                 stats.systemHealth > 80 ? "Excelente" :
                                 stats.systemHealth > 50 ? "Buena" : "Requiere atención"}
                            </p>
                        </div>
                    </div>

                    {/* Main Dashboard */}
                    <div className="grid lg:grid-cols-3 gap-8">
                        {/* Elevators List */}
                        <div className="lg:col-span-1">
                            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/20 shadow-lg overflow-hidden">
                                <div className="p-6 border-b border-gray-100">
                                    <h2 className="text-xl font-bold text-gray-900 flex items-center">
                                        <div className="w-3 h-3 bg-gradient-to-r from-purple-600 to-violet-600 rounded-full mr-3"></div>
                                        Elevadores Monitoreados
                                    </h2>
                                </div>
                                <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                                    {elevators.map((elevator) => (
                                        <div
                                            key={elevator.id}
                                            onClick={() => setSelectedElevator(elevator.id)}
                                            className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 hover:shadow-md ${
                                                selectedElevator === elevator.id
                                                    ? 'bg-purple-50 border-purple-200 shadow-md'
                                                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                            }`}
                                        >
                                            <div className="flex items-center justify-between mb-2">
                                                <h3 className="font-semibold text-gray-900">{elevator.name}</h3>
                                                <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(elevator.status)}`}>
                                                    {getStatusIcon(elevator.status)}
                                                    <span className="ml-1 capitalize">{elevator.status}</span>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                                                <span>{elevator.location}</span>
                                                <span className="flex items-center">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    {elevator.lastUpdate}
                                                </span>
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center text-sm text-gray-600">
                                                    {getMovementIcon(elevator)}
                                                    <span className="ml-1">Piso {elevator.floor}</span>
                                                </div>
                                                <div className="text-xs text-purple-600 font-medium">
                                                    {elevator.trips} viajes
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Selected Elevator Details */}
                        <div className="lg:col-span-2">
                            {elevators.filter(e => e.id === selectedElevator).map(elevator => (
                                <div key={elevator.id} className="space-y-6">
                                    {/* Elevator Header */}
                                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-lg">
                                        <div className="flex items-center justify-between mb-6">
                                            <div>
                                                <h2 className="text-2xl font-bold text-gray-900 mb-2">{elevator.name}</h2>
                                                <p className="text-gray-600 flex items-center">
                                                    <MapPin className="w-4 h-4 mr-2" />
                                                    {elevator.location} - TVE, CABA
                                                </p>
                                            </div>
                                            <div className={`flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(elevator.status)}`}>
                                                {getStatusIcon(elevator.status)}
                                                <span className="ml-2 capitalize">{elevator.status}</span>
                                            </div>
                                        </div>

                                        {/* Metrics */}
                                        <div className="grid grid-cols-2 gap-6">
                                            <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl border border-purple-100">
                                                <div className="flex items-center justify-center mb-3">
                                                    {getMovementIcon(elevator)}
                                                    <span className="ml-2 text-purple-600 font-semibold">Piso Actual</span>
                                                </div>
                                                <div className="text-3xl font-bold text-gray-900 mb-1">{elevator.floor}</div>
                                                <div className="text-sm text-gray-600">de {elevator.maxFloor} pisos</div>
                                                <div className="mt-3 w-full bg-purple-200 rounded-full h-3">
                                                    <div
                                                        className="h-3 bg-gradient-to-r from-purple-500 to-violet-600 rounded-full transition-all duration-500"
                                                        style={{ width: `${(elevator.floor / elevator.maxFloor) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>

                                            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                                                <Zap className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                                                <div className="text-3xl font-bold text-gray-900 mb-1">{elevator.voltage.toFixed(0)}V</div>
                                                <div className="text-sm text-gray-600">Voltaje</div>
                                                <div className="mt-3 text-xs text-blue-600 font-medium">
                                                    {elevator.current.toFixed(1)}A corriente
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Additional Info */}
                                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-lg">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <Calendar className="w-5 h-5 mr-2 text-purple-600" />
                                            Información del Sistema
                                        </h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-sm text-gray-600 mb-1">Última Actualización</div>
                                                <div className="font-semibold text-gray-900">Hace {elevator.lastUpdate}</div>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-sm text-gray-600 mb-1">Temperatura</div>
                                                <div className="font-semibold text-gray-900">{elevator.temperature.toFixed(1)}°C</div>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded-xl">
                                                <div className="text-sm text-gray-600 mb-1">Estado Mantenimiento</div>
                                                <div className="font-semibold text-gray-900">{elevator.maintenance}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Bottom CTA */}
                    <div className="text-center mt-16">
                        <div className="bg-gradient-to-r from-purple-600 to-violet-600 rounded-3xl p-8 text-white">
                            <h3 className="text-2xl font-bold mb-4">¿Interesado en implementar un sistema similar?</h3>
                            <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
                                Nuestro equipo puede diseñar y implementar una solución IoT personalizada para el monitoreo de elevadores en tu edificio o complejo.
                            </p>
                            <Link to="/#contact">
                                <div className="inline-flex items-center px-8 py-4 bg-white text-purple-600 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                                    <span className="mr-2">Contactar Ahora</span>
                                    <ArrowLeft className="w-5 h-5 rotate-180" />
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}